import { useTranslate } from '@tolgee/react';
import { Drawer } from 'antd';
import { useAdjustRelateKnowledge } from './useAdjustRelateKnowledge';
import { SupplementKnowledgeDetailResponse } from 'services/api/supplementKnowledge';
import { useCleanExpandInCalculation } from '../useUpdateSeekSku';
import { EmptyPlaceholder } from '../EmptyPlaceholder';
import { ContentWithDetail } from './ContentWithDetail';
import { ContentWithMessage } from './ContentWithMessage';
import { useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { queries } from 'services/queries';
import { ContentWithNotSupport } from '../AIErrorReplyDrawer/ContentWithNotSupport';
import { FullLoading } from 'components/FullLoading';

interface Props {
  message: Conversation.Message;
  conversation: Conversation.Conversation;
  knowledgeQuery: Conversation.KnowledgeQueryMessage;
  supplementKnowledgeDetail: SupplementKnowledgeDetailResponse;
  onDone?: () => void;
  onDelayRefresh?: () => void;
  onRefresh?: () => void;
}

const Loader = (props: Props) => {
  const {
    message,
    conversation,
    knowledgeQuery,
    supplementKnowledgeDetail,
    onDone,
    onDelayRefresh,
    onRefresh,
  } = props;
  if (supplementKnowledgeDetail.id) {
    return (
      <ContentWithDetail
        message={message}
        conversation={conversation}
        supplementKnowledgeDetail={supplementKnowledgeDetail}
        onDone={onDone}
        onDelayRefresh={onDelayRefresh}
        onRefresh={onRefresh}
      />
    );
  }

  return (
    <ContentWithMessage
      message={message}
      conversation={conversation}
      knowledgeQuery={knowledgeQuery}
      supplementKnowledgeDetail={supplementKnowledgeDetail}
      onDone={onDone}
    />
  );
};

export const AdjustRelateKnowledgeDrawer = () => {
  const { t } = useTranslate();
  const { meta, visible, hide: hideAdjustRelateKnowledge } = useAdjustRelateKnowledge();
  const delayRefetchRef = useRef<NodeJS.Timeout | undefined>(undefined);
  const cleanExpandInCalculation = useCleanExpandInCalculation();

  const questionMsgId = meta?.knowledgeQuery?.msgId;
  const conversationId = meta?.conversation?.conversationId;
  const {
    data: supplementKnowledgeDetail,
    refetch: refetchSupplementKnowledgeDetail,
    isLoading: isLoadingSupplementKnowledgeDetail,
  } = useQuery({
    enabled: visible && !!meta && !!questionMsgId && !!conversationId,
    ...queries.qaPair.knowledgeDetailByMsgId({
      conversationId: conversationId || '',
      msgId: questionMsgId || '',
      source: 'AI_ERROR_REPLY',
    }),
  });

  const handleDelayRefresh = () => {
    if (delayRefetchRef.current) {
      clearTimeout(delayRefetchRef.current);
    }
    delayRefetchRef.current = setTimeout(() => {
      refetchSupplementKnowledgeDetail().then(() => {
        cleanExpandInCalculation();
      });
    }, 5000);
  };

  useEffect(() => {
    if (!visible && delayRefetchRef.current) {
      cleanExpandInCalculation();
      clearTimeout(delayRefetchRef.current);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  let content = <EmptyPlaceholder />;
  if (meta) {
    const { message, conversation, knowledgeQuery } = meta;
    const { itemInfo } = knowledgeQuery;
    if (itemInfo) {
      if (isLoadingSupplementKnowledgeDetail) {
        content = <FullLoading />;
      } else if (supplementKnowledgeDetail) {
        if (supplementKnowledgeDetail.reviewStatus === 'AI_LEARNING_UNSUPPORTED') {
          content = (
            <ContentWithNotSupport
              hideChatPanel
              conversation={conversation}
              dataSource={supplementKnowledgeDetail}
              onRefresh={refetchSupplementKnowledgeDetail}
              onDelayRefresh={handleDelayRefresh}
              onClose={hideAdjustRelateKnowledge}
            />
          );
        } else {
          content = (
            <Loader
              message={message}
              conversation={conversation}
              knowledgeQuery={knowledgeQuery}
              supplementKnowledgeDetail={supplementKnowledgeDetail}
              onDone={hideAdjustRelateKnowledge}
              onDelayRefresh={handleDelayRefresh}
              onRefresh={refetchSupplementKnowledgeDetail}
            />
          );
        }
      }
    }
  }

  return (
    <Drawer
      title={t('调整相关知识')}
      width={640}
      bodyStyle={{
        padding: 0,
      }}
      visible={visible}
      onClose={hideAdjustRelateKnowledge}
    >
      {content}
    </Drawer>
  );
};
